import {DataFrameColumnDef, DataFrameColumns} from 'data-frames/DataFrameTypes';
import {ResultSchema} from 'results/assay-schemas/AssaySchemaConstants';
import {FIELD_TYPE_TO_LEAF_TYPE, SchemaField} from 'schemas/fields/SchemaFieldConstants';

import {DataFrameColumnToFieldMapping} from './DataMappingTypes';
import getColumnsThatMayBeMappedToField from './getColumnsThatMayBeMappedToField';

type MatchFn = (field: SchemaField, columnDef: DataFrameColumnDef) => boolean;

const NAME_MATCH_FN: MatchFn = (field, columnDef) =>
  field.displayName.toUpperCase() === columnDef.displayName.toUpperCase();

const EXACT_TYPE_MATCH_FN: MatchFn = (field, columnDef) =>
  FIELD_TYPE_TO_LEAF_TYPE[field.fieldType] === columnDef.type.leafType;

const LOOSE_MATCH_FN: MatchFn = () => true;

function createMapping(field: SchemaField, columnDef: DataFrameColumnDef): DataFrameColumnToFieldMapping {
  return {inputColumnSystemName: columnDef.systemName, outputFieldId: field.apiId};
}

function removePairFromPossibleMatchGraph(
  field: SchemaField,
  columnDef: DataFrameColumnDef,
  possibleMatchGraph: Map<SchemaField, Set<DataFrameColumnDef>>
): void {
  possibleMatchGraph.delete(field);
  for (const columnDefSet of possibleMatchGraph.values()) {
    columnDefSet.delete(columnDef);
  }
}

function findMappings(
  possibleMatchGraph: Map<SchemaField, Set<DataFrameColumnDef>>,
  matchFilterFunction: (field: SchemaField, columnDef: DataFrameColumnDef) => boolean
): Array<DataFrameColumnToFieldMapping> {
  const mappings: Array<DataFrameColumnToFieldMapping> = [];
  for (const [field, columnDefs] of possibleMatchGraph.entries()) {
    const bestMatches = [...columnDefs.values()].filter((columnDef) => matchFilterFunction(field, columnDef));
    if (bestMatches.length === 1) {
      mappings.push(createMapping(field, bestMatches[0]));
      removePairFromPossibleMatchGraph(field, bestMatches[0], possibleMatchGraph);
    }
  }
  return mappings;
}

/**
 * Create a default mapping from data frame columns to schema fields.
 *
 * Matches will be made in three rounds of matching:
 * 1. Columns and Fields with matching names (case-insensitive) and compatible types
 * 2. Columns and Fields with closely matching types (e.g. DECIMAL to FLOAT)
 * 3. Columns and Fields with compatible types (e.g. DATE to TEXT)
 *
 * To avoid defaulting matches the user did not intend, at each round a field will only be matched to a column
 * if there is only one matching column.
 */
export default function getDefaultColumnMapping(
  dataFrameColumns: DataFrameColumns,
  schema: ResultSchema
): Array<DataFrameColumnToFieldMapping> {
  const possibleMatchGraph = new Map<SchemaField, Set<DataFrameColumnDef>>(
    schema.fields.map((field) => [field, new Set(getColumnsThatMayBeMappedToField(dataFrameColumns, field))])
  );

  const mappings = [NAME_MATCH_FN, EXACT_TYPE_MATCH_FN, LOOSE_MATCH_FN].flatMap((matchFn) =>
    findMappings(possibleMatchGraph, matchFn)
  );

  return mappings;
}
